import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineCalculate,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdSuperscript
} from 'react-icons/md';
import moment from 'moment';
import Button from 'components/Button';
import TextInput from 'components/TextInput';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';
import { getDeviceOS } from 'global/os';
import { generatePassageHTML } from 'global/passageHighlight';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Reference from 'pages/private/DigitalSAT/Module/Reference';
import Shortcuts from 'pages/private/DigitalSAT/Module/Shortcuts';
import DesmosCalculatorPanel from 'components/DesmosCalculatorPanel';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};


function AssignmentReview() {
    const [studentName, setStudentName] = useState('');
    const [name, setName] = useState('');
    const [testType, setTestType] = useState('');
    const [subject, setSubject] = useState('');
    const [questions, setQuestions] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [annotations, setAnnotations] = useState([]);
    const [explanationOpen, setExplanationOpen] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { assignmentId } = useParams();
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else {
            // exit the review
            navigate(-1);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('assignmentReview__passages');
        let question = document.getElementById('assignmentReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const generateAnnotation = () => {
        const newAnnotations = [ ...answers[questionIndex].annotations ];

        for (let i = 0; i < newAnnotations.length; i++) {
            const {
                passageId,
                startIndex,
                endIndex
            } = newAnnotations[i];

            const passage = document.getElementById(passageId);

            // Stem annotations reference a question id with no matching
            // passage element on the review page; skip this one rather than
            // abandoning the remaining annotations.
            if (!passage) continue;

            const { passageHTML, passagePreview } = generatePassageHTML(passage, startIndex, endIndex);

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    const handleAnnotationMouseOver = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.left = event.clientX + 'px';
            bubble.style.top = event.clientY + 'px';
            bubble.style.display = 'block';
        }

        return false;
    }

    const handleAnnotationMouseOut = (
        _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.display = 'none';
        }

        return false;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            // console.log(wrappingTexts[i]);
            if (subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'KeyT':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyV':
                //     markQuestion();
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                case 'KeyR':
                    if (subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit5':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit6':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                // case 'Digit7':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit8':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit9':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit0':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                // case 'KeyG':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                case 'KeyH':
                    if (subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                // case 'KeyV':
                //     markQuestion();
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }
    
            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.assignmentReview__popupHeaderIcon') || event.target.closest('.assignmentReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.assignmentReview__popupHeaderIcon') || event.target.closest('.assignmentReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    };

    useEffect(() => {
        axios.get(`questions/assignment/review/${assignmentId}/`)
            .then(res => {
                // console.log(res.data);
                setStudentName(res.data.studentName);
                setName(res.data.name);
                setTestType(res.data.testType);
                setSubject(res.data.subject);
                setQuestions(res.data.questions);
                setAnswers(res.data.answers);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(() => {
        if (questions) {
            setShortcutKeyPressed(true);

            const referenceDragEl = document.getElementById('referenceDrag');
            const referenceHeaderEl = document.getElementById('referenceHeader');
            if (referenceDragEl && referenceHeaderEl) {
                dragMove(referenceDragEl, referenceHeaderEl);
            }
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        }
    }, [questions]);

    useLayoutEffect(() => {
        if (questions) {
            renderKatex();
            generateAnnotation();
        }
    }, [questions, questionIndex]);

    useEffect(() => {
        if (questions) {
            renderKatex();
        }
    }, [annotations, explanationOpen]);

    useEffect(() => {
        if (getDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (getDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (getDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (getDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    if (questions === null) return <Loader />;

    return (
        <div className='assignmentReviewBox' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    Reviewing {name} - {organization.name}
                </title>
            </Helmet>
            <div className='assignmentReview'>
                <div className='assignmentReview__header'>
                    <div className='assignmentReview__titleGroup'>
                        <button className='assignmentReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='assignmentReview__title'>
                                {name}
                            </h1>
                            {/* {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='assignmentReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='assignmentReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='assignmentReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'assignmentReview__moduleSelectDropdownItem--selected'
                                                : 'assignmentReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            } */}
                        </button>
                        {/* <button className='assignmentReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='assignmentReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='assignmentReview__directionsIcon' />
                            }
                        </button> */}
                    </div>
                    <div className='assignmentReview__timerGroup'>
                        <p className='assignmentReview__timer'>
                            {formatTime(answers[questionIndex].timeSpent)}
                        </p>
                        <p className='assignmentReview__timerText'>
                            Time spent
                        </p>
                    </div>
                    <div className='assignmentReview__actionGroup'>
                        {subject === 'Math' && <>
                            <button
                                className={referenceOpen
                                    ? 'assignmentReview__action--active'
                                    : 'assignmentReview__action'
                                }
                                onClick={() => {
                                    if (calculatorOpen) setCalculatorOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='assignmentReview__actionIcon' />
                                <p className='assignmentReview__actionText'>Reference</p>
                            </button>
                            <button 
                                className={calculatorOpen
                                    ? 'assignmentReview__action--active'
                                    : 'assignmentReview__action'
                                }
                                onClick={() => {
                                    if (referenceOpen) setReferenceOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='assignmentReview__actionIcon' />
                                <p className='assignmentReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className={shortcutsOpen
                                ? 'assignmentReview__action--active'
                                : 'assignmentReview__action'
                            }
                            onClick={() => {
                                if (calculatorOpen) setCalculatorOpen(false);
                                if (referenceOpen) setReferenceOpen(false);
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='assignmentReview__actionIcon' />
                            <p className='assignmentReview__actionText'>Shortcuts</p>
                        </button>
                        <button
                            className='assignmentReview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='assignmentReview__actionIcon' />
                            <p className='assignmentReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {/* {directionsOpen &&
                        <div className='assignmentReview__directions' ref={directionsRef}>
                            <div className='assignmentReview__directionsBubble'>
                                <p className='katex assignmentReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='assignmentReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    } */}
                    <DesmosCalculatorPanel
                        open={calculatorOpen}
                        onClose={() => setCalculatorOpen(false)}
                        headerClassName='assignmentReview__popupHeader'
                    />
                    <div id='referenceDrag' className={referenceOpen
                        ? 'assignmentReview__popup--reference'
                        : 'assignmentReview__popup--hidden'
                    }>
                        <div id='referenceHeader' className='assignmentReview__popupHeader'>
                            <h3 className='assignmentReview__popupHeaderText'>Reference</h3>
                            <MdClose className='assignmentReview__popupHeaderIcon' onClick={() => setReferenceOpen(false)} />
                        </div>
                        <Reference />
                    </div>
                    <div id='shortcutsDrag' className={shortcutsOpen
                        ? 'assignmentReview__popup--shortcuts'
                        : 'assignmentReview__popup--hidden'
                    }>
                        <div id='shortcutsHeader' className='assignmentReview__popupHeader'>
                            <h3 className='assignmentReview__popupHeaderText'>
                                Keyboard shortcuts{getDeviceOS() === 'windows'
                                    ? ': Windows'
                                    : getDeviceOS() === 'mac_os'
                                    ? ': MacOS'
                                    : ''
                                }
                            </h3>
                            <MdClose className='assignmentReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                    {(organization.customizations.find(customization => customization === 'Show Explanation') && questions[questionIndex].explanation) &&
                        <div id='explanationDrag' className={explanationOpen
                            ? 'digitalReview__popup--explanation'
                            : 'digitalReview__popup--hidden'

                        }>
                            <div id='explanationHeader' className='digitalReview__popupHeader'>
                                <h3 className='digitalReview__popupHeaderText'>
                                    Explanation
                                </h3>
                                <MdClose className='digitalReview__popupHeaderIcon' onClick={() => setExplanationOpen(false)} />
                            </div>
                            <div className='digitalReview__popupBody'>
                                <p className='katex digitalReview__popupBodyText'>
                                    {questions[questionIndex].explanation}
                                </p>
                            </div>
                        </div>
                    }
                </div>
                <div className='assignmentReview__dottedLine'></div>
                <div className='assignmentReview__test'>
                    {questions[questionIndex].passages.length > 0 && <>
                        <div className='assignmentReview__material'>
                            <div className='assignmentReview__materialContent'>
                                <div id='assignmentReview__passages' className='assignmentReview__passages'>
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='assignmentReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                    {questions[questionIndex].passages.map(({ id, passage }) => (
                                        <div key={id} className='assignmentReview__passage'>
                                            <p
                                                id={id}
                                                className='katex assignmentReview__passageText'
                                            >
                                                {passage}
                                            </p>
                                            {annotations.map((annotation, a) => <>
                                                <div
                                                    // className='katex assignmentReview__passageAnnotation'
                                                    className={`katex assignmentReview__passageAnnotation
                                                        ${annotation.color && 'assignmentReview__passageAnnotation--' + COLORS[annotation.color]}
                                                        ${annotation.underline && 'assignmentReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                    `}
                                                    onMouseOver={(event) => handleAnnotationMouseOver(event, `assignmentReview__passageAnnotationBubble${a}`)}
                                                    onMouseOut={(event) => handleAnnotationMouseOut(event, `assignmentReview__passageAnnotationBubble${a}`)}
                                                    dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                ></div>
                                                {annotation.annotation &&
                                                    <p
                                                        id={`assignmentReview__passageAnnotationBubble${a}`}
                                                        className='assignmentReview__passageAnnotationBubble'
                                                    >
                                                        {annotation.annotation}
                                                    </p>
                                                }
                                            </>)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='assignmentReview__verticalLine'></div>
                    </>}
                    <div className='assignmentReview__question'>
                        <div className='assignmentReview__questionContent'>
                            <div className='assignmentReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'assignmentReview__questionNumber--correct'
                                    : 'assignmentReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='assignmentReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='assignmentReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='assignmentReview__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                {(organization.customizations.find(customization => customization === 'Show Explanation') && questions[questionIndex].explanation) &&
                                    <button
                                        className={explanationOpen
                                            ? 'digitalReview__showExplanation--active'
                                            : 'digitalReview__showExplanation'
                                        }
                                        onClick={() => setExplanationOpen(!explanationOpen)}
                                    >
                                        Explanation
                                    </button>
                                }
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                    <button
                                        className={showAnswer
                                            ? 'digitalReview__showAnswer--active'
                                            : 'digitalReview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                }
                            </div>
                            <div id='assignmentReview__questionChoices' className='assignmentReview__questionChoices'>
                                {subject === 'Math' &&
                                    questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='assignmentReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))
                                }
                                <p className='assignmentReview__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='assignmentReview__questionChoiceWrapper'>
                                                <button
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'assignmentReview__questionChoice--correct'
                                                        : answers[questionIndex].answer == choice.id
                                                        ? (choice.isCorrect 
                                                            ? 'assignmentReview__questionChoice--selected'
                                                            : 'assignmentReview__questionChoice--wrong'
                                                        )
                                                        : 'assignmentReview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='assignmentReview__questionChoiceOutline'></div>
                                                    <p className='assignmentReview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='assignmentReview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <p className='assignmentReview__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                :   <div className='assignmentReview__questionAnswerReview'>
                                        <TextInput
                                            className='assignmentReview__questionAnswer'
                                            inputClassName='assignmentReview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={answers[questionIndex].answer
                                                ? answers[questionIndex].answer
                                                : ''
                                            }
                                            onChange={() => {}}
                                        />
                                        {showAnswer &&
                                            <p className='assignmentReview__questionCorrectAnswer'>
                                                {questions[questionIndex].correctAnswers.toString().replaceAll(',', ', ')}
                                            </p>
                                        }
                                    </div>
                                }
                                <div className='assignmentReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='assignmentReview__dottedLine'></div>
                <div className='assignmentReview__footer'>
                    <p className='assignmentReview__tester'>
                        {studentName}
                    </p>
                    <button
                        className='assignmentReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='assignmentReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='assignmentReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='assignmentReview__buttons'>
                        <Button
                            className='assignmentReview__button'
                            secondary
                            disabled={questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='assignmentReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='assignmentReview__navigator' ref={navigatorRef}>
                            <div className='assignmentReview__navigatorBubble'>
                                <h2 className='assignmentReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='assignmentReview__navigatorLegends'>
                                    <div className='assignmentReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='assignmentReview__navigatorLegendCurrent' />
                                        <p className='assignmentReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='assignmentReview__navigatorLegend'>
                                        <div className='assignmentReview__navigatorLegendCorrect'></div>
                                        <p className='assignmentReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='assignmentReview__navigatorLegend'>
                                        <div className='assignmentReview__navigatorLegendWrong'></div>
                                        <p className='assignmentReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='assignmentReview__navigatorLegend'>
                                        <MdOutlineBookmark className='assignmentReview__navigatorLegendMarked' />
                                        <p className='assignmentReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='assignmentReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'assignmentReview__navigatorItem--correct'
                                                : 'assignmentReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='assignmentReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='assignmentReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='assignmentReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='assignmentReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default AssignmentReview;